import { Link } from 'react-router-dom';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import {
 ArrowRight,
 Terminal,
 Cpu,
 ShieldCheck,
 Zap,
 Globe,
 ChevronRight,
} from 'lucide-react';

export function LandingPage() {
 return (
  <div className='min-h-screen flex flex-col bg-background text-foreground font-sans selection:bg-primary/20 selection:text-primary'>
   {/* Grid Lines Background */}
   <div className='fixed inset-0 z-0 pointer-events-none flex justify-center'>
    <div className='w-full max-w-[1400px] h-full border-x border-border/40 grid grid-cols-4 lg:grid-cols-12 gap-0'>
     {Array.from({ length: 12 }).map((_, i) => (
      <div
       key={i}
       className='border-r border-border/20 h-full hidden lg:block last:border-r-0'
      />
     ))}
    </div>
   </div>

   {/* Navigation - Technical & Sharp */}
   <header className='fixed top-0 w-full z-50 border-b border-border bg-background/80 backdrop-blur-md'>
    <div className='max-w-[1400px] mx-auto h-16 grid grid-cols-12 items-center'>
     {/* Logo Section */}
     <div className='col-span-3 lg:col-span-2 pl-6 flex items-center h-full border-r border-border'>
      <div className='font-mono font-bold tracking-tighter text-xl flex items-center gap-2'>
       <div className='w-3 h-3 bg-primary animate-pulse' />
       MOCK.
      </div>
     </div>

     {/* Empty Spacer */}
     <div className='hidden lg:block lg:col-span-7 h-full' />

     {/* Actions */}
     <div className='col-span-9 lg:col-span-3 flex h-full'>
      <div className='flex-1 flex items-center justify-center border-l border-border h-full px-4'>
       <ThemeToggle />
      </div>
      <Link
       to='/login'
       className='flex-1 hidden md:flex items-center justify-center border-l border-border hover:bg-secondary/50 transition-colors uppercase text-xs font-mono tracking-widest font-bold'
      >
       Sign In
      </Link>
      <Link
       to='/register'
       className='flex-[1.5] flex items-center justify-center border-l border-border bg-foreground text-background hover:bg-foreground/90 transition-all uppercase text-xs font-mono tracking-widest font-bold group'
      >
       Start{' '}
       <ArrowRight className='ml-2 w-3 h-3 group-hover:translate-x-1 transition-transform' />
      </Link>
     </div>
    </div>
   </header>

   <main className='flex-1 relative z-10 pt-16'>
    {/* Hero Section - Industrial Scale */}
    <section className='border-b border-border relative overflow-hidden'>
     <div className='max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 min-h-[85vh]'>
      {/* Hero Content */}
      <div className='col-span-12 lg:col-span-8 p-6 lg:p-12 flex flex-col justify-between border-r border-border relative'>
       {/* Top Badge */}
       <div>
        <div className='inline-flex items-center gap-2 text-xs font-mono text-primary uppercase tracking-widest border border-primary/30 px-3 py-1 mb-8'>
         <Terminal className='w-3 h-3' />
         <span>System Online // v2.0.4</span>
        </div>
       </div>

       {/* Main Heading */}
       <div className='space-y-6'>
        <h1 className='text-4xl md:text-7xl lg:text-9xl font-bold leading-[0.9] tracking-tighter'>
         MASTER
         <br />
         <span className='text-muted-foreground'>THE</span>
         <br />
         INTERVIEW
        </h1>
        <p className='text-lg md:text-2xl text-muted-foreground max-w-xl leading-relaxed'>
         High-fidelity interview simulations for ambitious professionals.
         <br />
         Calibrated to top-tier industry standards. Zero latency.
        </p>
       </div>

       {/* Bottom Actions */}
       <div className='pt-12 flex flex-col sm:flex-row gap-0 border-t border-border -mx-6 lg:-mx-12 mt-12 bg-secondary/10'>
        <Link
         to='/register'
         className='flex-1 p-8 hover:bg-primary hover:text-primary-foreground transition-all group border-b sm:border-b-0 sm:border-r border-border'
        >
         <div className='flex justify-between items-start mb-4'>
          <Cpu className='w-6 h-6' />
          <ArrowRight className='w-5 h-5 opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all' />
         </div>
         <div className='font-mono text-xs uppercase opacity-70 mb-1'>
          Initialize
         </div>
         <div className='text-2xl font-bold tracking-tight'>
          Start Simulation
         </div>
        </Link>
        <Link
         to='/demo'
         className='flex-1 p-8 hover:bg-secondary transition-all group'
        >
         <div className='flex justify-between items-start mb-4'>
          <Globe className='w-6 h-6' />
         </div>
         <div className='font-mono text-xs uppercase opacity-70 mb-1'>
          Explore
         </div>
         <div className='text-2xl font-bold tracking-tight'>Global Metrics</div>
        </Link>
       </div>
      </div>

      {/* Hero Visual / Code Block */}
      <div className='col-span-12 lg:col-span-4 bg-secondary/5 flex flex-col border-t lg:border-t-0 p-8 font-mono text-sm leading-6 overflow-hidden relative'>
       <div className='absolute inset-0 bg-[radial-gradient(#27272a_1px,transparent_1px)] [background-size:16px_16px] opacity-20'></div>
       <div className='space-y-4 text-muted-foreground relative z-10'>
        <div className='flex gap-2 text-xs border-b border-border pb-4 mb-4'>
         <span className='text-primary'>●</span>
         <span>simulation_config.json</span>
        </div>
        <p>
         <span className='text-blue-400'>const</span>{' '}
         <span className='text-yellow-400'>scenario</span> ={' '}
         <span className='text-purple-400'>await</span> system.
         <span className='text-blue-400'>load</span>({'{'}
        </p>
        <p className='pl-4'>
         role: <span className='text-green-400'>'Product Lead'</span>,
        </p>
        <p className='pl-4'>
         industry: <span className='text-green-400'>'Fintech'</span>,
        </p>
        <p className='pl-4'>
         focus: <span className='text-red-400'>'Strategy'</span>
        </p>
        <p>{'}'});</p>
        <br />
        <p>
         <span className='text-muted'>// Establishing connection...</span>
        </p>
        <p>
         <span className='text-primary'>✓ Context verified</span>
        </p>
        <p>
         <span className='text-primary'>✓ Protocols active</span>
        </p>
        <div className='pt-4 flex gap-2'>
         <span className='animate-pulse'>_</span>
        </div>
       </div>
      </div>
     </div>
    </section>

    {/* Features Grid - Grid Borders */}
    <section className='border-b border-border bg-background'>
     <div className='max-w-[1400px] mx-auto'>
      <div className='grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-border'>
       {[
        {
         idx: '01',
         icon: Cpu,
         title: 'Adaptive Context',
         desc:
          'Engine constructs specific scenarios from job descriptions and industry patterns.',
        },
        {
         idx: '02',
         icon: Zap,
         title: 'Real-time Latency',
         desc:
          'Sub-100ms response time for fluid, natural professional discourse.',
        },
        {
         idx: '03',
         icon: ShieldCheck,
         title: 'Granular Analysis',
         desc:
          'Detailed breakdown of response accuracy, communication clarity, and structural logic.',
        },
       ].map((feature) => (
        <div
         key={feature.idx}
         className='p-8 lg:p-12 group hover:bg-secondary/5 transition-colors'
        >
         <div className='font-mono text-xs text-muted-foreground mb-8 flex justify-between tracking-widest'>
          <span>{feature.idx} /</span>
          <feature.icon className='w-5 h-5 text-primary' />
         </div>
         <h3 className='text-2xl font-bold mb-4 group-hover:translate-x-1 transition-transform'>
          {feature.title}
         </h3>
         <p className='text-muted-foreground leading-relaxed'>{feature.desc}</p>
        </div>
       ))}
      </div>
     </div>
    </section>

    {/* System Stats / Trust */}
    <section className='py-24 border-b border-border bg-secondary/5'>
     <div className='max-w-[1400px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center'>
      <div>
       <h2 className='text-4xl md:text-5xl font-bold mb-6 tracking-tight'>
        ENGINEERED FOR
        <br />
        <span className='text-primary'>PERFORMANCE</span>
       </h2>
       <p className='text-lg text-muted-foreground max-w-md'>
        Built on a globally distributed edge network. Designed to simulate the
        pressure of high-stakes career moments.
       </p>

       <div className='mt-12 grid grid-cols-2 gap-8 border-t border-border pt-8'>
        <div>
         <div className='text-4xl font-bold font-mono mb-1'>10k+</div>
         <div className='text-xs font-mono uppercase text-muted-foreground tracking-widest'>
          Sessions Logged
         </div>
        </div>
        <div>
         <div className='text-4xl font-bold font-mono mb-1'>99.9%</div>
         <div className='text-xs font-mono uppercase text-muted-foreground tracking-widest'>
          System Uptime
         </div>
        </div>
       </div>
      </div>

      <div className='grid grid-cols-2 gap-px bg-border border border-border'>
       {['Google', 'Meta', 'Amazon', 'Netflix', 'Uber', 'Stripe'].map((co) => (
        <div
         key={co}
         className='bg-background p-8 flex items-center justify-center grayscale hover:grayscale-0 transition-all cursor-crosshair'
        >
         <span className='text-xl font-bold opacity-40'>{co}</span>
        </div>
       ))}
      </div>
     </div>
    </section>

    {/* Footer - Directory Style */}
    <footer className='py-20 px-6 max-w-[1400px] mx-auto'>
     <div className='grid grid-cols-1 md:grid-cols-4 gap-12'>
      <div className='col-span-1 md:col-span-1 space-y-4'>
       <div className='font-mono font-bold tracking-tighter text-lg'>MOCK.</div>
       <p className='text-sm text-muted-foreground'>
        // Version 2.0.4
        <br />
        // System Status: <span className='text-primary'>Operational</span>
       </p>
      </div>

      {['Product', 'Company', 'Legal'].map((col) => (
       <div key={col} className='space-y-4'>
        <h4 className='font-mono text-xs uppercase tracking-widest text-muted-foreground'>
         {col}
        </h4>
        <ul className='space-y-2'>
         <li>
          <a
           href='#'
           className='text-sm hover:text-primary transition-colors flex items-center group'
          >
           Overview{' '}
           <ChevronRight className='w-3 h-3 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-1 transition-all' />
          </a>
         </li>
         <li>
          <a
           href='#'
           className='text-sm hover:text-primary transition-colors flex items-center group'
          >
           Changelog{' '}
           <ChevronRight className='w-3 h-3 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-1 transition-all' />
          </a>
         </li>
         <li>
          <a
           href='#'
           className='text-sm hover:text-primary transition-colors flex items-center group'
          >
           Documentation{' '}
           <ChevronRight className='w-3 h-3 opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-1 transition-all' />
          </a>
         </li>
        </ul>
       </div>
      ))}
     </div>
     <div className='mt-20 pt-8 border-t border-border flex justify-between items-center font-mono text-xs text-muted-foreground uppercase tracking-widest'>
      <p>© 2024 FUSION LABS INT.</p>
      <p>SFO /// CA</p>
     </div>
    </footer>
   </main>
  </div>
 );
}
